import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { NavLinkItem } from './types';

export interface NavProps {
  links: NavLinkItem[];
  navigateToSection: (sectionId: string) => void;
  topOffsetPx: number;
}

/**
 * Desktop navigation: section links and page links, centered/left-aligned.
 * Keyboard navigable with visible focus states; high-contrast text on primary background.
 */
export function Nav({ links, navigateToSection, topOffsetPx }: NavProps) {
  const location = useLocation();

  return (
    <nav
      className="sticky z-40 hidden lg:flex justify-center space-x-0.5 sm:space-x-1 md:space-x-2 lg:space-x-3 xl:space-x-4 px-1 sm:px-2 md:px-3 lg:px-4 xl:px-6 py-2.5 bg-primary shadow-md border-t border-primary flex-wrap"
      style={{ top: `${topOffsetPx}px` }}
      aria-label="Main navigation"
    >
      {links.map(({ href, label, isSection }, index) => {
        const pathNoTrailing = location.pathname.replace(/\/$/, '') || '/';
        const hrefNoTrailing = href.replace(/\/$/, '') || '/';
        const isActiveSection =
          isSection && pathNoTrailing === '/' && location.hash === `#${href}`;
        const isActiveLink = !isSection && pathNoTrailing === hrefNoTrailing;
        const isActive = isActiveSection || isActiveLink;
        const linkClass =
          'cursor-pointer text-xs sm:text-sm font-medium transition-colors duration-200 px-1 py-1 focus:outline-none focus:ring-0 ' +
          (isActive
            ? 'text-white border-b-2 border-white'
            : 'text-white/90 hover:text-white');

        return (
          <Fragment key={href}>
            {isSection ? (
              <button
                type="button"
                onClick={() => navigateToSection(href)}
                className={linkClass}
                aria-label={`Navigate to ${label} section`}
                aria-current={isActive ? 'page' : undefined}
              >
                {label}
              </button>
            ) : (
              <Link
                to={href}
                className={linkClass}
                aria-current={isActive ? 'page' : undefined}
              >
                {label}
              </Link>
            )}
            {index < links.length - 1 && (
              <span
                className="text-white/40 text-xs px-0.5 select-none"
                aria-hidden="true"
              >
                |
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
